// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { getCustomTypeSession } from "@pacific/App/Store/Selectors/SessionSelectors";
import LazyImage, { LazyImageProps } from "@pacific/MobiusOverrides/LazyImage/LazyImage";
import { headingsFontConfig } from "@pacific/XDL/PreStyle/typography";
import { xBlueprintName } from "@pacific/XDL/types";
import { color, ellipsis, xScreen } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import { mergeWithCss } from "@pacific/XDL/Utilities/mergeWithCss";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    categoryId?: string;
    showImage?: boolean;
    extendedStyles?: CategoryItemStyles;
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        isAuthenticated: xIsAuthenticated(state),
        session: getCustomTypeSession(state),
        currentBillTo: getCurrentBillToState(state).value,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;
export interface CategoryItemStyles {
    wrapper?: ClickablePresentationProps;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    categoryTitle?: TypographyPresentationProps;
    categoryNameLink?: LinkPresentationProps;
}

export const categoryItemStyles: CategoryItemStyles = {
    wrapper: {
        css: css`
            width: auto;
            height: auto;
            display: flex;
            flex-direction: column;
            align-items: center;
            &:focus {
                outline: none;
            }
            &:hover {
                .category-title {
                    color: ${color("common.ui8")};
                }
            }
        `,
    },
    imageWrapper: {
        css: css`
            box-shadow: 0 -4px 0 0 ${color("primary.main")};
            width: 198px;
            height: 198px;

            ${xScreen("lte", "sm")} {
                max-width: 100%;
                height: auto;
            }
        `,
    },
    image: {
        transformations: {
            width: 198,
        },
        css: css`
            object-fit: cover;
            width: 198px;
            height: 198px;
            img: {
                width: 100%;
            }
        `,
    },
    categoryTitle: {
        size: 15,
        weight: "bold",
        fontFamily: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily,
        css: css`
            letter-spacing: 0;
            line-height: 17px;
            display: block;
            text-align: center;
            margin-top: 12px;
            max-width: 198px;
            ${ellipsis(2)}
        `,
    },
};

const CategoryItem: FC<Props> = ({
    categoryId,
    showImage = true,
    extendedStyles,
    isAuthenticated,
    session,
    currentBillTo,
}) => {
    const category = useAppSelector(state => getCategoryState(state, categoryId).value);
    const [styles] = useState(() => mergeWithCss(categoryItemStyles, extendedStyles));
    if (!category) {
        return null;
    }

    const _GaProductCategoryClick = (title: string, url: string) => {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const googleAnalyticsData = {
            event: "product_category_click",
            click_text: title,
            destination: url,
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: (session as any)?.salesperson?.name || "",
        };
        dataLayer.push(googleAnalyticsData);
    };

    return (
        <Clickable
            {...styles.wrapper}
            href={category.path}
            data-test-selector={`category-item_${category.id}`}
            aria-label="category image clickable"
            onClick={() => _GaProductCategoryClick(category.shortDescription, category.path)}
        >
            {showImage && (
                <StyledWrapper {...styles.imageWrapper} className="img-wrapper">
                    <LazyImage
                        src={category.smallImagePath}
                        altText={category.imageAltText || category.shortDescription}
                        {...styles.image}
                    />
                </StyledWrapper>
            )}
            <Typography
                {...styles.categoryTitle}
                className="category-title"
                aria-label={category.shortDescription}
                title={category.shortDescription}
            >
                {category.shortDescription}
            </Typography>
        </Clickable>
    );
};

export default connect(mapStateToProps)(CategoryItem);
